import type {DeepReadonly} from '@/lib/typings/readonly';
import type {
  AnalyticsData,
  Transaction,
  TransactionItem,
  TransactionItemCategory,
  TransactionItemGa3,
  TransactionItemGa4,
} from '@gfiber/typesmith';
import {randomString} from '@gfiber/typesmith/src/google/helper';

import type {ProductWidget, ProductWidgetType} from '../TypesOpenApi';

export const buildTransaction = (
  affiliation: string,
  monthlyRevenue = 0.0,
  upfrontRevenue = 0.0,
  purchaseIdLength = 31,
): Transaction => {
  if (!affiliation) {
    throw new Error('Ecommerce affiliation must not be null or empty.');
  }

  // Random string used to ensure purchase is unique and not a duplicate,
  // but avoids PII.
  const purchaseId = randomString(purchaseIdLength);

  return {
    id: purchaseId,
    affiliation,
    revenue: monthlyRevenue,
    upfrontRevenue,
  };
};

export const buildTransactionItems = (
  products: DeepReadonly<ProductWidget[]>,
): TransactionItem[] => {
  const items: TransactionItem[] = [];
  let itemIndex = 0;

  for (const product of products) {
    const category = getTransactionItemCategory(
      product.productType || 'PRODUCT_TYPE_UNSPECIFIED',
    );
    if (!category) continue;
    const subline = !product.isActualProduct ? product.productType : '';
    const category3 = getTransactionItemCategory3(product);
    const category4 = getTransactionItemCategory4();
    items.push({
      id: product.productId || '',
      name: product.productName || '',
      category,
      index: itemIndex++,
      item_category: category,
      item_category2: subline,
      item_category3: category3,
      item_category4: category4,
      price:
        product.chargeType === 'MONTHLY' ? product.productCost?.units || 0 : 0,
      upfrontPrice:
        product.chargeType === 'ONE_TIME' ? product.productCost?.units || 0 : 0,
      quantity: product.quantity || 0,
    });
  }
  return items;
};

const getTransactionItemCategory3 = (
  product: DeepReadonly<ProductWidget>,
): string | '' => {
  if (
    product.productType &&
    product.productType in ['INTERNET_PRODUCT', 'CPE_POINT_PRODUCT']
  ) {
    return product.productId || '';
  }
  return '';
};

const getTransactionItemCategory4 = ()// product: DeepReadonly<ProductWidget>,
: string | '' => {
  // BFF doesn't currently provide the hardware device type needed to determine this one
  // Legacy code for reference below

  // if (
  //   product.getProductLine() === Product.ProductLine.DEVICE &&
  //   product.getHardwareInfo()?.getType() === DeviceType.NETWORK_BOX
  // ) {
  //   return productNetworkBoxTypeToString(
  //     product
  //       .getHardwareInfo()
  //       ?.getNetworkDeviceProperties()
  //       ?.getNetworkBoxType(),
  //   );
  // }
  return '';
};

const getTransactionItemCategory = (
  productType: ProductWidgetType,
): TransactionItemCategory | undefined => {
  // TODO seems like we are missing quite a few from the original
  // https://source.corp.google.com/piper///depot/google3/isp/fiber/customer/portal2/client/modularsignup/modularsignup_app/services/product_manager.ts;l=988;rcl=689090065
  switch (productType) {
    case 'INTERNET_PRODUCT':
      return 'plan';
    case 'CPE_POINT_PRODUCT':
      return 'device';
    case 'PHONE_PRODUCT':
      return 'phone';
    case 'STATIC_IP_PRODUCT':
    case 'STREAMING_PRODUCT':
      return 'add on';
    default:
      return;
  }
};

export const buildTransactionData = (
  transaction: Transaction,
  items: TransactionItem[],
  pageName?: string,
  routerType?: string,
  additionalData?: AnalyticsData,
): AnalyticsData => {
  // CSV of all IDs in items
  const productList = items.map(item => item.id).join(',');
  // CSV of all categories in items
  const serviceList = Array.from(
    new Set(items.map(items => items.category)),
  ).join(',');
  const transactionId = transaction.id;
  const value = transaction.upfrontRevenue;
  const currency = 'USD';

  const transactionItemGa4 = transactionItemToTransactionItemGa4(items);
  const transactionItemGa3 = transactionItemToTransactionItemGa3(items);
  const ecommerce = {
    productList: productList,
    serviceList: serviceList,
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: transactionItemGa4,
    purchase: {
      actionField: transaction,
      products: transactionItemGa3,
    },
  };

  const data: AnalyticsData = {
    event: 'trackPurchase',
    ecommerce: ecommerce,
  };
  if (pageName) {
    data.pageName = pageName;
  }
  if (routerType) {
    data.routerType = routerType;
  }
  if (additionalData) {
    Object.assign(data, additionalData);
  }
  return data;
};

const transactionItemToTransactionItemGa3 = (
  items: TransactionItem[],
): TransactionItemGa3[] => {
  const transactionItemGa3: TransactionItemGa3[] = [];
  for (const item of items) {
    transactionItemGa3.push({
      id: item.id,
      name: item.name,
      category: item.category,
      price: item.price,
      upfrontPrice: item.upfrontPrice,
      quantity: item.quantity,
    });
  }
  return transactionItemGa3;
};

const transactionItemToTransactionItemGa4 = (
  items: TransactionItem[],
): TransactionItemGa4[] => {
  const transactionItemGa4: TransactionItemGa4[] = [];
  for (const item of items) {
    transactionItemGa4.push({
      item_id: item.id,
      item_name: item.name,
      affiliation: item.affiliation,
      coupon: item.coupon,
      discount: item.discount,
      index: item.index,
      item_brand: item.item_brand,
      item_category: item.item_category,
      item_category2: item.item_category2,
      item_category3: item.item_category3,
      item_category4: item.item_category4,
      item_category5: item.item_category5,
      item_list_id: item.item_list_id,
      item_list_name: item.item_list_name,
      item_variant: item.item_variant,
      location_id: item.location_id,
      price: item.price,
      quantity: item.quantity,
    });
  }
  return transactionItemGa4;
};
